import { useState } from "react";
import { button, input, primary, Notice, errorText } from "../agency/ui";
import {
  PLACEMENTS,
  chinaInput,
  chinaTimestamp,
  validatePayload,
  type Payload,
  type Placement,
} from "./model";
import { uploadCreative } from "./service";
import { CreativePreview } from "./CreativePreview";
export function AdEditor({
  initial,
  busy,
  canPublish,
  onSave,
  onCancel,
}: {
  initial?: Payload;
  busy: boolean;
  canPublish: boolean;
  onSave: (p: Payload, note: string, publish: boolean) => Promise<void>;
  onCancel: () => void;
}) {
  const [payload, setPayload] = useState<Payload>(() =>
    initial
      ? { attribution_enabled: false, click_id_param: "channel_id", ...structuredClone(initial) }
      : {
          title: "",
          market_id: "CN",
          link_url: "",
          attribution_enabled: true,
          click_id_param: "channel_id",
          start_at: new Date().toISOString(),
          end_at: new Date(Date.now() + 7 * 86400000).toISOString(),
          placements: [
            {
              placement: "home_carousel",
              image_url: "",
              sort_order: 0,
            },
          ],
        },
  );
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState<Placement | null>(null);
  const edit = (key: Placement, change: object) =>
    setPayload((p) => ({
      ...p,
      placements: p.placements.map((x) =>
        x.placement === key ? { ...x, ...change } : x,
      ),
    }));
  const save = async (publish: boolean) => {
    const invalid = validatePayload(payload);
    if (invalid) {
      setError(invalid);
      return;
    }
    setError("");
    await onSave({ ...payload, placements: payload.placements.map(({ placement, image_url, sort_order }) => ({ placement, image_url, sort_order })) }, note, publish);
  };
  return (
    <div className="space-y-5">
      <Notice error={error} />
      <div className="grid gap-4 md:grid-cols-2">
        <label className="text-sm space-y-1">
          广告名称（仅供后台管理）
          <input
            className={`${input} w-full`}
            value={payload.title}
            maxLength={120}
            onChange={(e) => setPayload({ ...payload, title: e.target.value })}
          />
        </label>
        <label className="text-sm space-y-1">
          落地页链接
          <input
            className={`${input} w-full`}
            placeholder="https://"
            value={payload.link_url}
            onChange={(e) =>
              setPayload({ ...payload, link_url: e.target.value })
            }
          />
        </label>
        <label className="text-sm space-y-1">
          开始时间（中国时间）
          <input
            type="datetime-local"
            className={`${input} w-full`}
            value={chinaInput(payload.start_at)}
            onChange={(e) =>
              setPayload({
                ...payload,
                start_at: chinaTimestamp(e.target.value),
              })
            }
          />
        </label>
        <label className="text-sm space-y-1">
          结束时间（中国时间）
          <input
            type="datetime-local"
            className={`${input} w-full`}
            value={chinaInput(payload.end_at)}
            onChange={(e) =>
              setPayload({ ...payload, end_at: chinaTimestamp(e.target.value) })
            }
          />
        </label>
      </div>
      <section className="rounded-lg border border-gray-200 p-4 space-y-3">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            checked={payload.attribution_enabled ?? false}
            disabled={busy}
            onChange={(e) => setPayload({ ...payload, attribution_enabled: e.target.checked })}
          />
          开启渠道标记
        </label>
        <p className="text-xs text-gray-500">
          开启后向合作方落地页传递本广告固定的渠道编号。同一广告在不同用户、广告位和修订版本中使用同一个渠道编号，不传递内部点击编号，也不接收注册或成交回传。
        </p>
        <label className="block text-sm">
          渠道参数名
          <input
            className={`${input} mt-1 block w-full max-w-sm`}
            value={payload.click_id_param ?? "channel_id"}
            disabled={busy || !payload.attribution_enabled}
            maxLength={32}
            placeholder="channel_id"
            onChange={(e) => setPayload({ ...payload, click_id_param: e.target.value })}
          />
        </label>
        <p className="text-xs text-gray-500">
          默认 channel_id；以英文字母开头，仅含字母、数字、下划线，最多 32 位，不能与落地页已有查询参数重名。修改渠道标记开关或参数名后需审核新修订，批准后生效。
        </p>
        {!initial && <p className="text-sm text-gray-500">渠道编号：保存广告后自动生成，生成后固定不变。</p>}
      </section>
      <p className="text-xs text-gray-500">
        投放市场：中国区 ·
        越南区暂未开放。选择一个或两个广告位；每个广告位独立配置图片与排序。
      </p>
      {(Object.keys(PLACEMENTS) as Placement[]).map((key) => {
        const creative = payload.placements.find((p) => p.placement === key);
        return (
          <section
            key={key}
            className="rounded-lg border border-gray-200 p-4 space-y-3"
          >
            <label className="flex items-center gap-2 font-medium text-sm">
              <input
                type="checkbox"
                checked={!!creative}
                onChange={(e) =>
                  setPayload({
                    ...payload,
                    placements: e.target.checked
                      ? [
                          ...payload.placements,
                          {
                            placement: key,
                            image_url: "",
                            sort_order: 0,
                          },
                        ]
                      : payload.placements.filter((p) => p.placement !== key),
                  })
                }
              />
              {PLACEMENTS[key]}
            </label>
            {creative && (
              <>
                <div className="flex flex-wrap items-end gap-3">
                  <label className="text-sm flex-1 min-w-48">
                    已上传素材
                    <input
                      className={`${input} w-full mt-1`}
                      value={creative.image_url}
                      placeholder="上传 PNG / JPEG / WebP / GIF，最大 5 MB"
                      readOnly
                    />
                  </label>
                  {creative.image_url && (
                    <button
                      className={button}
                      disabled={busy || !!uploading}
                      onClick={() => edit(key, { image_url: "" })}
                    >
                      移除图片
                    </button>
                  )}
                  <label className={`${button} cursor-pointer`}>
                    {uploading === key ? "上传中…" : "上传图片"}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="sr-only"
                      disabled={!!uploading || busy}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        if (!file) return;
                        setUploading(key);
                        setError("");
                        try {
                          const result = await uploadCreative(file);
                          edit(key, { image_url: result.url });
                        } catch (err) {
                          setError(errorText(err));
                        } finally {
                          setUploading(null);
                        }
                      }}
                    />
                  </label>
                </div>
                <div className="grid gap-3 md:grid-cols-[140px]">
                  <label className="text-sm">
                    展示排序
                    <input
                      type="number"
                      min={0}
                      step={1}
                      className={`${input} w-full mt-1`}
                      value={creative.sort_order}
                      onChange={(e) =>
                        edit(key, { sort_order: Number(e.target.value) })
                      }
                    />
                    <span className="text-xs text-gray-400">
                      数字越小越靠前
                    </span>
                  </label>
                </div>
              </>
            )}
          </section>
        );
      })}
      {initial && (
        <label className="block text-sm">
          本次修改说明
          <input
            className={`${input} mt-1 w-full`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="说明本次素材或排期调整"
          />
        </label>
      )}
      <details className="rounded-lg bg-gray-50 p-4" open>
        <summary className="cursor-pointer text-sm font-medium mb-3">
          素材预览
        </summary>
        <CreativePreview payload={payload} />
      </details>
      <div className="flex flex-wrap gap-3">
        <button
          className={primary}
          disabled={busy || !!uploading}
          onClick={() => void save(false)}
        >
          {busy ? "保存中…" : "保存草稿"}
        </button>
        {canPublish && (
          <button
            className={button}
            disabled={busy || !!uploading}
            onClick={() => void save(true)}
          >
            保存并发布
          </button>
        )}
        <button
          className={button}
          disabled={busy || !!uploading}
          onClick={onCancel}
        >
          取消
        </button>
      </div>
    </div>
  );
}
